import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'livag_cookie_consent';

export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
        applyConsent(saved);
      } catch (e) {
        setShowBanner(true);
      }
    }
  }, []);

  const applyConsent = (prefs) => {
    // Enable/disable tracking based on consent
    if (prefs.analytics && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted'
      });
    }
    if (prefs.marketing && window.posthog) {
      window.posthog.opt_in_capturing();
    } else if (window.posthog) {
      window.posthog.opt_out_capturing();
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    applyConsent(allAccepted);
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(necessaryOnly));
    setPreferences(necessaryOnly);
    applyConsent(necessaryOnly);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(preferences));
    applyConsent(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay – decorative dim only, must NOT intercept clicks so /admin login etc. remain clickable */}
      <div className="fixed inset-0 bg-black/30 z-[9998] pointer-events-none" aria-hidden="true" />
      
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-2xl overflow-hidden">
          {!showSettings ? (
            /* Main Banner */
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-12 h-12 bg-[#264F77]/10 rounded-full items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-[#264F77]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-[#264F77] mb-2">
                    Wir verwenden Cookies
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Wir nutzen Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
                    Einige sind notwendig, andere helfen uns, die Website zu verbessern.{' '}
                    <Link to="/datenschutz" className="text-[#264F77] underline hover:no-underline">
                      Mehr erfahren
                    </Link>
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleAcceptAll}
                      className="px-6 py-2.5 bg-[#264F77] text-white font-semibold rounded-lg hover:bg-[#1e3d5c] transition-colors min-h-[44px]"
                    >
                      Alle akzeptieren
                    </button>
                    <button
                      onClick={handleAcceptNecessary}
                      className="px-6 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors min-h-[44px]"
                    >
                      Nur notwendige
                    </button>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="px-6 py-2.5 text-[#264F77] font-semibold rounded-lg border border-[#264F77]/30 hover:bg-[#264F77]/5 transition-colors min-h-[44px] inline-flex items-center justify-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Einstellungen
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Settings Panel */
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#264F77]">Cookie-Einstellungen</h3>
                <button 
                  onClick={() => setShowSettings(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Schliessen"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-semibold text-gray-800">Notwendige Cookies</h4>
                    <p className="text-sm text-gray-600">Für die Grundfunktionen der Website erforderlich.</p>
                  </div>
                  <div className="w-12 h-6 bg-[#264F77] rounded-full relative cursor-not-allowed">
                    <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
                  </div>
                </div>
                
                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-semibold text-gray-800">Analyse-Cookies</h4>
                    <p className="text-sm text-gray-600">Helfen uns, die Website-Nutzung zu verstehen.</p>
                  </div>
                  <button
                    onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                    className={`w-12 h-6 rounded-full relative transition-colors ${preferences.analytics ? 'bg-[#264F77]' : 'bg-gray-300'}`}
                    aria-label="Analyse-Cookies umschalten"
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${preferences.analytics ? 'right-1' : 'left-1'}`} />
                  </button>
                </div>
                
                {/* Marketing Cookies */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-semibold text-gray-800">Marketing-Cookies</h4>
                    <p className="text-sm text-gray-600">Für personalisierte Inhalte und Werbung.</p>
                  </div>
                  <button
                    onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                    className={`w-12 h-6 rounded-full relative transition-colors ${preferences.marketing ? 'bg-[#264F77]' : 'bg-gray-300'}`}
                    aria-label="Marketing-Cookies umschalten"
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${preferences.marketing ? 'right-1' : 'left-1'}`} />
                  </button>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 px-6 py-2.5 bg-[#264F77] text-white font-semibold rounded-lg hover:bg-[#1e3d5c] transition-colors min-h-[44px]"
                >
                  Auswahl speichern
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors min-h-[44px]"
                >
                  Alle akzeptieren
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
